import type {
  QuoteApprovalPlacement,
  QuoteLine,
  QuoteLineInput,
  QuoteLinesFrequency,
} from '@catalyst-psa/shared';
import {
  QUOTE_APPROVAL_PLACEMENTS,
  QUOTE_APPROVAL_PLACEMENT_LABELS,
  QUOTE_LINES_FREQUENCIES,
  QUOTE_LINES_FREQUENCY_LABELS,
  QUOTE_SECTION_COUNT,
} from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { useProductOptions } from '../products/api.js';
import { ProductPicker } from '../products/ProductPicker.js';

const LINES_POSITION_OPTIONS = Array.from({ length: QUOTE_SECTION_COUNT + 1 }, (_, i) => ({
  value: String(i),
  label: i === 0 ? 'Before section 1' : `After section ${i}`,
}));

const APPROVAL_PLACEMENT_OPTIONS = QUOTE_APPROVAL_PLACEMENTS.map((p) => ({
  value: p,
  label: QUOTE_APPROVAL_PLACEMENT_LABELS[p],
}));

const LINES_FREQUENCY_OPTIONS = QUOTE_LINES_FREQUENCIES.map((f) => ({
  value: f,
  label: QUOTE_LINES_FREQUENCY_LABELS[f],
}));

/** Editable line row (string fields for form inputs) shared by quotes + templates. */
export interface LineForm {
  /** Local list key (not persisted). */
  key: string;
  /** Which product-lines section (1 or 2) the line renders in. */
  group: number;
  productId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  isOptional: boolean;
  /** Pre-checks this optional line for the customer on the delivery page. */
  preselect: boolean;
  bundleKey: string;
  bundleMinChoose: string;
  bundleMaxChoose: string;
  /** Display-only comment row: full-width description, no qty/price, not in totals. */
  isComment: boolean;
  /** Bundle header/title row: carries the bundle's checkbox + title (bundleKey groups members). */
  isBundleHeader: boolean;
  /** On a bundle header: hide the member lines on the delivery page/PDF. */
  bundleLinesHidden: boolean;
}

let lineKeyCounter = 0;

function nextLineKey(): string {
  lineKeyCounter += 1;
  return `line-${lineKeyCounter}`;
}

function emptyLine(group: number): LineForm {
  return {
    key: nextLineKey(),
    group,
    productId: '',
    description: '',
    quantity: '1',
    unitPrice: '0',
    isOptional: false,
    preselect: false,
    bundleKey: '',
    bundleMinChoose: '',
    bundleMaxChoose: '',
    isComment: false,
    isBundleHeader: false,
    bundleLinesHidden: false,
  };
}

function emptyComment(group: number): LineForm {
  return { ...emptyLine(group), quantity: '0', unitPrice: '0', isComment: true };
}

let bundleKeyCounter = 0;

/** A new bundle header row with a unique default bundle key. */
function emptyBundleHeader(group: number, existingKeys: Set<string>): LineForm {
  let key = '';
  do {
    bundleKeyCounter += 1;
    key = `Bundle ${bundleKeyCounter}`;
  } while (existingKeys.has(key));
  return {
    ...emptyLine(group),
    quantity: '0',
    unitPrice: '0',
    isBundleHeader: true,
    bundleKey: key,
  };
}

/** Builds the editable line forms from a quote/template's stored lines. */
export function toLineForms(lines: QuoteLine[] | undefined): LineForm[] {
  return (lines ?? []).map((line) => ({
    key: nextLineKey(),
    group: line.lineGroup === 2 ? 2 : 1,
    productId: line.productId ?? '',
    description: line.description,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    isOptional: line.isOptional,
    preselect: line.preselect,
    bundleKey: line.bundleKey ?? '',
    bundleMinChoose: line.bundleMinChoose != null ? String(line.bundleMinChoose) : '',
    bundleMaxChoose: line.bundleMaxChoose != null ? String(line.bundleMaxChoose) : '',
    isComment: line.isComment,
    isBundleHeader: line.isBundleHeader,
    bundleLinesHidden: line.bundleLinesHidden,
  }));
}

/** Converts the forms back into the API input shape (section 1 first, then section 2). */
export function toLineInputs(forms: LineForm[]): QuoteLineInput[] {
  const ordered = [...forms.filter((l) => l.group !== 2), ...forms.filter((l) => l.group === 2)];
  return ordered.map((line) => ({
    productId: line.isBundleHeader ? null : line.productId || null,
    description: line.description.trim(),
    quantity: line.isBundleHeader ? '0' : line.quantity.trim() || '1',
    unitPrice: line.isBundleHeader ? '0' : line.unitPrice.trim() || '0',
    lineGroup: line.group,
    isOptional: !line.isBundleHeader && line.isOptional,
    preselect: (line.isBundleHeader || line.isOptional) && line.preselect,
    bundleKey: line.bundleKey.trim() || null,
    bundleMinChoose:
      !line.isBundleHeader && line.bundleMinChoose.trim() ? Number(line.bundleMinChoose) : null,
    bundleMaxChoose:
      !line.isBundleHeader && line.bundleMaxChoose.trim() ? Number(line.bundleMaxChoose) : null,
    isComment: line.isComment,
    isBundleHeader: line.isBundleHeader,
    bundleLinesHidden: line.isBundleHeader && line.bundleLinesHidden,
  }));
}

/** Editable column/total labels of one lines section (always populated in the form). */
export interface LineLabelsForm {
  /** Section heading (first table column header); defaults to the frequency label. */
  header: string;
  qty: string;
  unitPrice: string;
  amount: string;
  total: string;
  /** Hide the column (or total row) on the delivery page, PDF, and SOW view. */
  hideQty: boolean;
  hideUnitPrice: boolean;
  hideAmount: boolean;
  hideTotal: boolean;
}

/** The built-in labels used when nothing custom is stored. */
export function defaultLineLabels(frequency: QuoteLinesFrequency): LineLabelsForm {
  return {
    header: QUOTE_LINES_FREQUENCY_LABELS[frequency],
    qty: 'Qty',
    unitPrice: 'Unit price',
    amount: 'Amount',
    total: `${QUOTE_LINES_FREQUENCY_LABELS[frequency]} total`,
    hideQty: false,
    hideUnitPrice: false,
    hideAmount: false,
    hideTotal: false,
  };
}

/** Builds the label form from stored values, preloading defaults for null fields. */
export function toLineLabelsForm(
  stored: {
    sectionLabel: string | null;
    qtyLabel: string | null;
    unitPriceLabel: string | null;
    amountLabel: string | null;
    totalLabel: string | null;
    qtyHidden: boolean;
    unitPriceHidden: boolean;
    amountHidden: boolean;
    totalHidden: boolean;
  },
  frequency: QuoteLinesFrequency,
): LineLabelsForm {
  const defaults = defaultLineLabels(frequency);
  return {
    header: stored.sectionLabel ?? defaults.header,
    qty: stored.qtyLabel ?? defaults.qty,
    unitPrice: stored.unitPriceLabel ?? defaults.unitPrice,
    amount: stored.amountLabel ?? defaults.amount,
    total: stored.totalLabel ?? defaults.total,
    hideQty: stored.qtyHidden,
    hideUnitPrice: stored.unitPriceHidden,
    hideAmount: stored.amountHidden,
    hideTotal: stored.totalHidden,
  };
}

/** Payload values: blank or default-matching labels persist as null (= use default). */
export function toLineLabelInputs(
  labels: LineLabelsForm,
  frequency: QuoteLinesFrequency,
): {
  sectionLabel: string | null;
  qtyLabel: string | null;
  unitPriceLabel: string | null;
  amountLabel: string | null;
  totalLabel: string | null;
  qtyHidden: boolean;
  unitPriceHidden: boolean;
  amountHidden: boolean;
  totalHidden: boolean;
} {
  const defaults = defaultLineLabels(frequency);
  const orNull = (value: string, def: string): string | null => {
    const trimmed = value.trim();
    return trimmed && trimmed !== def ? trimmed : null;
  };
  return {
    sectionLabel: orNull(labels.header, defaults.header),
    qtyLabel: orNull(labels.qty, defaults.qty),
    unitPriceLabel: orNull(labels.unitPrice, defaults.unitPrice),
    amountLabel: orNull(labels.amount, defaults.amount),
    totalLabel: orNull(labels.total, defaults.total),
    qtyHidden: labels.hideQty,
    unitPriceHidden: labels.hideUnitPrice,
    amountHidden: labels.hideAmount,
    totalHidden: labels.hideTotal,
  };
}

/** Per-group frequency + placement controls (controlled by the host page). */
export interface LineGroupControls {
  frequency: QuoteLinesFrequency;
  placement: string;
  /** Force a page break before/after this lines table (PDF + printed delivery page). */
  pageBreakBefore: boolean;
  pageBreakAfter: boolean;
  /** Column/total labels shown on the delivery page, PDF, and SOW view. */
  labels: LineLabelsForm;
  onFrequencyChange: (frequency: QuoteLinesFrequency) => void;
  onPlacementChange: (placement: string) => void;
  onPageBreakBeforeChange: (checked: boolean) => void;
  onPageBreakAfterChange: (checked: boolean) => void;
  onLabelsChange: (patch: Partial<LineLabelsForm>) => void;
}

/** Placement of the delivery page's "Approve this quote" block. */
export interface ApprovalPlacementControls {
  placement: QuoteApprovalPlacement;
  onChange: (placement: QuoteApprovalPlacement) => void;
}

/**
 * The two product-line sections editor shared by quotes and quote templates: per-group
 * frequency/placement, add/remove/reorder, product pick (pre-fills description + unit
 * price), qty/price, optional flag, and bundle key with Min/Max choose.
 */
export function QuoteLinesEditor({
  lines,
  onChange,
  groups,
  approval,
}: {
  lines: LineForm[];
  onChange: (next: LineForm[]) => void;
  /** Controls for lines section 1 and 2 (in that order). */
  groups: [LineGroupControls, LineGroupControls];
  /** Delivery-page approval block placement; omitted when the host doesn't expose it. */
  approval?: ApprovalPlacementControls;
}): JSX.Element {
  const productsQuery = useProductOptions();
  const products = productsQuery.data ?? [];

  const updateLine = (key: string, patch: Partial<LineForm>): void => {
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /** Moves a line up/down within its section (persisted in this order on save). */
  const moveLine = (group: number, index: number, direction: -1 | 1): void => {
    const inGroup = lines.filter((l) => l.group === group);
    const target = index + direction;
    if (target < 0 || target >= inGroup.length) return;
    const reordered = [...inGroup];
    [reordered[index], reordered[target]] = [reordered[target]!, reordered[index]!];
    const other = lines.filter((l) => l.group !== group);
    onChange(group === 2 ? [...other, ...reordered] : [...reordered, ...other]);
  };

  /** Picking a product pre-fills description + unit price (still editable). */
  const handleProductPick = (key: string, productId: string): void => {
    const product = products.find((p) => p.id === productId);
    updateLine(key, {
      productId,
      ...(product
        ? {
            description: product.customerDescription || product.name,
            unitPrice: product.unitPrice,
          }
        : {}),
    });
  };

  return (
    <>
      {/* Two line sections — array order within a section is the sort order. */}
      {[1, 2].map((group) => {
        const groupLines = lines.filter((l) => l.group === group);
        const controls = groups[group === 2 ? 1 : 0];
        return (
          <div
            key={group}
            id={`quote-lines-section-${group}`}
            className="mt-6 scroll-mt-6 rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="border-b border-slate-200 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">
                Lines section {group} ({groupLines.length})
              </span>
            </div>
            {/* Section-level options grouped together so the header stays uncluttered. */}
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Section Settings
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-44">
                  <TextField
                    label="Section Label"
                    value={controls.labels.header}
                    maxLength={64}
                    onChange={(e) => controls.onLabelsChange({ header: e.target.value })}
                  />
                </div>
                <div className="w-36">
                  <SelectField
                    label="Frequency"
                    value={controls.frequency}
                    options={LINES_FREQUENCY_OPTIONS}
                    onChange={(e) =>
                      controls.onFrequencyChange(e.target.value as QuoteLinesFrequency)
                    }
                  />
                </div>
                <div className="w-44">
                  <SelectField
                    label="Placement"
                    value={controls.placement}
                    options={LINES_POSITION_OPTIONS}
                    onChange={(e) => controls.onPlacementChange(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-4 pb-2">
                  <CheckboxField
                    label="Page break before this section"
                    checked={controls.pageBreakBefore}
                    onChange={(e) => controls.onPageBreakBeforeChange(e.target.checked)}
                  />
                  <CheckboxField
                    label="Page break after this section"
                    checked={controls.pageBreakAfter}
                    onChange={(e) => controls.onPageBreakAfterChange(e.target.checked)}
                  />
                </div>
              </div>
            </div>
            {/* Column labels share the line-row grid so each sits above its column. */}
            <div className="border-b border-slate-200 px-4 py-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Line Settings
              </p>
              <div className="grid items-end gap-3 lg:grid-cols-12">
                <div className="hidden lg:col-span-6 lg:block" />
                <div className="flex flex-col gap-1 lg:col-span-1">
                  <CheckboxField
                    label="Hide"
                    checked={controls.labels.hideQty}
                    onChange={(e) => controls.onLabelsChange({ hideQty: e.target.checked })}
                  />
                  <TextField
                    label="Qty label"
                    value={controls.labels.qty}
                    maxLength={64}
                    onChange={(e) => controls.onLabelsChange({ qty: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1 lg:col-span-1">
                  <CheckboxField
                    label="Hide"
                    checked={controls.labels.hideUnitPrice}
                    onChange={(e) => controls.onLabelsChange({ hideUnitPrice: e.target.checked })}
                  />
                  <TextField
                    label="Unit price label"
                    value={controls.labels.unitPrice}
                    maxLength={64}
                    onChange={(e) => controls.onLabelsChange({ unitPrice: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1 lg:col-span-1">
                  <CheckboxField
                    label="Hide"
                    checked={controls.labels.hideAmount}
                    onChange={(e) => controls.onLabelsChange({ hideAmount: e.target.checked })}
                  />
                  <TextField
                    label="Amount label"
                    value={controls.labels.amount}
                    maxLength={64}
                    onChange={(e) => controls.onLabelsChange({ amount: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1 lg:col-span-1">
                  <CheckboxField
                    label="Hide"
                    checked={controls.labels.hideTotal}
                    onChange={(e) => controls.onLabelsChange({ hideTotal: e.target.checked })}
                  />
                  <TextField
                    label="Total label"
                    value={controls.labels.total}
                    maxLength={64}
                    onChange={(e) => controls.onLabelsChange({ total: e.target.value })}
                  />
                </div>
              </div>
            </div>
            {groupLines.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-500">
                No lines in this section — it is skipped on the quote.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {groupLines.map((line, index) => (
                  <div
                    key={line.key}
                    className={`grid gap-3 px-4 py-3 lg:grid-cols-12 ${line.isBundleHeader ? 'bg-sky-50/60' : ''}`}
                  >
                    <div
                      className={`flex items-center gap-2 ${
                        line.isComment
                          ? 'lg:col-span-10'
                          : line.isBundleHeader
                            ? 'lg:col-span-6'
                            : 'lg:col-span-3'
                      }`}
                    >
                      <div className="flex flex-col">
                        <button
                          type="button"
                          aria-label="Move line up"
                          className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                          disabled={index === 0}
                          onClick={() => moveLine(group, index, -1)}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          aria-label="Move line down"
                          className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                          disabled={index === groupLines.length - 1}
                          onClick={() => moveLine(group, index, 1)}
                        >
                          ▼
                        </button>
                      </div>
                      <div className="min-w-0 flex-1">
                        {line.isComment ? (
                          <TextField
                            label="Comment (spans the whole table row)"
                            required
                            value={line.description}
                            onChange={(e) => updateLine(line.key, { description: e.target.value })}
                          />
                        ) : line.isBundleHeader ? (
                          <TextField
                            label="Bundle title (the customer's checkbox sits on this row)"
                            required
                            value={line.description}
                            onChange={(e) => updateLine(line.key, { description: e.target.value })}
                          />
                        ) : (
                          <ProductPicker
                            label="Product"
                            products={products}
                            value={line.productId}
                            emptyLabel="No product (custom line)"
                            onChange={(productId) => handleProductPick(line.key, productId)}
                          />
                        )}
                      </div>
                    </div>
                    {line.isComment ? (
                      <div className="flex items-end justify-end gap-2 pb-2 lg:col-span-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:bg-rose-50"
                          onClick={() => onChange(lines.filter((other) => other.key !== line.key))}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : line.isBundleHeader ? (
                      <>
                        <div className="lg:col-span-2">
                          <TextField
                            label="Bundle key"
                            required
                            value={line.bundleKey}
                            onChange={(e) => updateLine(line.key, { bundleKey: e.target.value })}
                          />
                          <p className="mt-1 text-xs text-slate-500">
                            Lines with this bundle key belong to the bundle.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-end justify-end gap-4 pb-2 lg:col-span-4">
                          <label
                            className="flex items-center gap-1.5 text-xs text-slate-600"
                            title="Hide the bundle's lines on the delivery page and PDF — the bundle total still shows on this row"
                          >
                            <input
                              type="checkbox"
                              checked={line.bundleLinesHidden}
                              onChange={(e) =>
                                updateLine(line.key, { bundleLinesHidden: e.target.checked })
                              }
                              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            />
                            Hide bundle lines
                          </label>
                          <label
                            className="flex items-center gap-1.5 text-xs text-slate-600"
                            title="Pre-checks this bundle for the customer on the delivery page (they can still switch)"
                          >
                            <input
                              type="checkbox"
                              checked={line.preselect}
                              onChange={(e) => updateLine(line.key, { preselect: e.target.checked })}
                              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            />
                            Preselect
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => onChange(lines.filter((other) => other.key !== line.key))}
                          >
                            Remove
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                    <div className="lg:col-span-3">
                      <TextField
                        label="Description"
                        required
                        value={line.description}
                        onChange={(e) => updateLine(line.key, { description: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <TextField
                        label="Qty"
                        value={line.quantity}
                        onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <TextField
                        label="Unit price"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <TextField
                        label="Bundle key"
                        value={line.bundleKey}
                        onChange={(e) => updateLine(line.key, { bundleKey: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <TextField
                        label="Min choose"
                        value={line.bundleMinChoose}
                        onChange={(e) => updateLine(line.key, { bundleMinChoose: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <TextField
                        label="Max choose"
                        value={line.bundleMaxChoose}
                        onChange={(e) => updateLine(line.key, { bundleMaxChoose: e.target.value })}
                      />
                    </div>
                    {/* Stacked vertically so the Remove button can't overflow the row. */}
                    <div className="flex flex-col items-start justify-end gap-1.5 pb-2 lg:col-span-1">
                      <label className="flex items-center gap-1.5 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={line.isOptional}
                          onChange={(e) =>
                            updateLine(line.key, {
                              isOptional: e.target.checked,
                              ...(e.target.checked ? {} : { preselect: false }),
                            })
                          }
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        Optional
                      </label>
                      {line.isOptional ? (
                        <label
                          className="flex items-center gap-1.5 text-xs text-slate-600"
                          title="Pre-checks this option for the customer on the delivery page (they can still deselect it)"
                        >
                          <input
                            type="checkbox"
                            checked={line.preselect}
                            onChange={(e) => updateLine(line.key, { preselect: e.target.checked })}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          />
                          Preselect
                        </label>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 hover:bg-rose-50"
                        onClick={() => onChange(lines.filter((other) => other.key !== line.key))}
                      >
                        Remove
                      </Button>
                    </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* The only Add buttons — kept at the bottom of the section. */}
            <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 px-4 py-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onChange([...lines, emptyLine(group)])}
              >
                Add line
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title="A display-only row that spans the table — no qty/price, not in totals, never copied to the opportunity"
                onClick={() => onChange([...lines, emptyComment(group)])}
              >
                Add comment
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title="A bundle header/title row — the customer chooses ONE bundle on the delivery page via the checkbox on this row. Give the member lines the same bundle key."
                onClick={() =>
                  onChange([
                    ...lines,
                    emptyBundleHeader(
                      group,
                      new Set(lines.map((l) => l.bundleKey.trim()).filter(Boolean)),
                    ),
                  ])
                }
              >
                Add bundle
              </Button>
            </div>
          </div>
        );
      })}

      {approval ? (
        <div
          id="quote-delivery-settings"
          className="mt-6 scroll-mt-6 rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div className="pb-2">
              <span className="text-sm font-medium text-slate-700">Delivery page</span>
              <p className="text-xs text-slate-500">
                Where the &ldquo;Approve this quote&rdquo; block sits on the customer&rsquo;s
                delivery page. Anchors that aren&rsquo;t present (an empty lines section, or a
                hidden/blank section) fall back to the end of the document.
              </p>
            </div>
            <div className="w-56">
              <SelectField
                label="Approval block placement"
                value={approval.placement}
                options={APPROVAL_PLACEMENT_OPTIONS}
                onChange={(e) => approval.onChange(e.target.value as QuoteApprovalPlacement)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
